import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useMappedInSettings } from '../../hooks/useMappedInSettings';

const MappedInSpaceSelector = ({
  value,
  onChange,
  onFloorChange, // Add callback for floor level
  onExternalIdChange, // Add callback for externalId
  label = 'Select MappedIn Space',
  placeholder = 'Search for a space...',
  buttonColor = '#000000',
  disabled = false,
  userId, // Add userId prop
}) => {
  const [spaces, setSpaces] = useState([]);
  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [mappedInAvailable, setMappedInAvailable] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch MappedIn settings
  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);

  // MappedIn configuration - use from settings or fallback to default
  const MAPPEDIN_OPTIONS = mappedInConfig || {
    key: 'mik_gxWE7PUdHxoK9qNlr555107ac',
    secret: 'mis_hvx40sGJZ2cuyzSDEHv55PtldjuszH20dVqhRkBV8R597271073',
    mapId: '69709560a8a681000bf10352',
  };

  console.log('MappedInSpaceSelector using config:', MAPPEDIN_OPTIONS);

  // Load MappedIn SDK and fetch spaces
  const loadMappedInAndFetchSpaces = async () => {
    if (initialized || configLoading || !mappedInConfig) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading MappedIn SDK...');

      // Try to import MappedIn SDK
      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;

      console.log('✅ MappedIn SDK loaded successfully');
      setMappedInAvailable(true);

      console.log('🔄 Fetching MappedIn spaces with options:', MAPPEDIN_OPTIONS);
      const mapData = await getMapData(MAPPEDIN_OPTIONS);

      console.log('✅ Map data received:', mapData);
      
      // Use enterprise-location instead of space (recommended by map provider)
      const enterpriseLocations = mapData.getByType('enterprise-location');
      console.log('📍 Raw enterprise locations:', enterpriseLocations);
      console.log('📍 Number of enterprise locations:', enterpriseLocations?.length || 0);

      if (!enterpriseLocations || enterpriseLocations.length === 0) {
        console.warn('⚠️ No enterprise locations found, falling back to spaces');
        // Fallback to spaces if no enterprise locations
        const spacesData = mapData.getByType('space');
        
        if (!spacesData || spacesData.length === 0) {
          console.warn('⚠️ No spaces found either');
          setError('No locations found in the MappedIn map. Please check the map configuration.');
          return;
        }

        // Format spaces data
        const formattedSpaces = spacesData
          .filter(space => space.name && space.name.trim() !== '')
          .map(space => ({
            id: space.id,
            name: space.name.trim(),
            description: space.description || '',
            floor: space.floor?.name || space.floor?.id || '',
            externalId: '', // Spaces don't have externalId
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        console.log(`✅ Formatted ${formattedSpaces.length} spaces (fallback):`, formattedSpaces);
        setSpaces(formattedSpaces);
        setFilteredSpaces(formattedSpaces);
        setInitialized(true);
        return;
      }

      // Format enterprise locations
      const formattedLocations = enterpriseLocations
        .filter(location => {
          const hasName = location.name && location.name.trim() !== '';
          if (!hasName) {
            console.log('🚫 Filtering out location without name:', location);
          }
          return hasName;
        })
        .map(location => ({
          id: location.id,
          name: location.name.trim(),
          description: location.description || '',
          floor: location.floor?.name || location.floor?.id || '',
          externalId: location.externalId || '', // Store externalId
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      console.log(`✅ Formatted ${formattedLocations.length} enterprise locations:`, formattedLocations);

      if (formattedLocations.length === 0) {
        setError('No valid locations found. All locations may be missing names.');
        return;
      }

      setSpaces(formattedLocations);
      setFilteredSpaces(formattedLocations);
      setInitialized(true);
    } catch (err) {
      console.error('❌ Error loading MappedIn or fetching spaces:', err);
      setMappedInAvailable(false);

      // More specific error messages
      let errorMessage = 'Failed to load MappedIn spaces. ';
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        errorMessage += 'Invalid API credentials.';
      } else if (err.message.includes('404') || err.message.includes('Not Found')) {
        errorMessage += 'Map not found. Check the mapId.';
      } else if (err.message.includes('network') || err.message.includes('fetch')) {
        errorMessage += 'Network error. Check your internet connection.';
      } else {
        errorMessage += err.message;
      }
      errorMessage += ' You can still enter a space name manually.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Initialize on component mount or when config changes
  useEffect(() => {
    if (!configLoading && mappedInConfig) {
      console.log('🚀 MappedInSpaceSelector mounted/config loaded, initializing...');
      loadMappedInAndFetchSpaces();
    }
  }, [configLoading, mappedInConfig]);

  // Filter spaces based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSpaces(spaces);
    } else {
      const filtered = spaces.filter(
        space =>
          space.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          space.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          space.floor.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSpaces(filtered);
      console.log(`🔍 Filtered ${filtered.length} spaces for term: "${searchTerm}"`);
    }
  }, [searchTerm, spaces]);

  // Set initial search term based on value
  useEffect(() => {
    if (value) {
      setSearchTerm(value);
    }
  }, [value]);

  // Handle input change
  const handleInputChange = event => {
    const newValue = event.target.value;
    setSearchTerm(newValue);

    // Always allow manual input
    onChange(newValue);

    // Show dropdown only if MappedIn is available and we have spaces
    if (mappedInAvailable && spaces.length > 0) {
      setIsOpen(true);
    }
  };

  // Handle space selection
  const handleSpaceSelect = space => {
    console.log('📍 Space selected:', space);
    setSearchTerm(space.name);
    setIsOpen(false);
    onChange(space.name);
    
    // Call floor change callback if provided
    if (onFloorChange && space.floor) {
      onFloorChange(space.floor);
      console.log('📍 Floor level set:', space.floor);
    }
    
    // Call externalId change callback if provided
    if (onExternalIdChange && space.externalId) {
      onExternalIdChange(space.externalId);
      console.log('📍 External ID set:', space.externalId);
    }
    
    inputRef.current?.blur();
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!disabled && mappedInAvailable && spaces.length > 0) {
      setIsOpen(true);
    }
  };

  // Handle input blur (with longer delay to allow for clicks)
  const handleInputBlur = () => {
    // Increased delay to ensure dropdown clicks register
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  // Handle click outside - improved version
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is outside both input and dropdown
      const clickedOutsideInput = inputRef.current && !inputRef.current.contains(event.target);
      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);

      if (clickedOutsideInput && clickedOutsideDropdown) {
        setIsOpen(false);
      }
    };

    // Only add listener when dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <FormControl fullWidth>
        <TextField
          ref={inputRef}
          label={label}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={mappedInAvailable ? placeholder : 'Enter space name manually'}
          disabled={disabled || loading}
          variant="outlined"
          InputProps={{
            endAdornment: loading && <CircularProgress size={20} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: buttonColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: buttonColor,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: buttonColor,
            },
          }}
        />
      </FormControl>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Debug: MappedIn Available: {mappedInAvailable ? 'Yes' : 'No'} | Spaces: {spaces.length} |
          Loading: {loading ? 'Yes' : 'No'} | Initialized: {initialized ? 'Yes' : 'No'}
        </Typography>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity={mappedInAvailable ? 'error' : 'warning'} sx={{ mt: 1 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <button
              onClick={() => {
                console.log('🔄 Retrying MappedIn initialization...');
                setError(null);
                setInitialized(false);
                setMappedInAvailable(false);
                setSpaces([]);
                setFilteredSpaces([]);
                loadMappedInAndFetchSpaces();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: buttonColor,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </Box>
        </Alert>
      )}

      {/* Success Message */}
      {mappedInAvailable && spaces.length > 0 && !error && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          ✓ Loaded {spaces.length} spaces from MappedIn
        </Typography>
      )}

      {/* Dropdown */}
      {isOpen && !error && mappedInAvailable && (
        <Paper
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 300,
            overflow: 'auto',
            mt: 1,
            border: `1px solid ${buttonColor}20`,
            boxShadow: 3,
          }}
        >
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading spaces...
              </Typography>
            </Box>
          ) : filteredSpaces.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? `No spaces found for "${searchTerm}"` : 'No spaces available'}
              </Typography>
            </Box>
          ) : (
            <List dense>
              {filteredSpaces.map(space => (
                <ListItem
                  key={space.id}
                  button
                  onMouseDown={e => {
                    e.preventDefault(); // Prevent input blur
                    handleSpaceSelect(space);
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: `${buttonColor}10`,
                    },
                    cursor: 'pointer',
                  }}
                >
                  <ListItemText
                    primary={space.name}
                    secondary={
                      <Box>
                        {space.description && (
                          <Typography variant="caption" display="block">
                            {space.description}
                          </Typography>
                        )}
                        {space.floor && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Floor: {space.floor}
                          </Typography>
                        )}
                        {space.externalId && (
                          <Typography variant="caption" color="primary" display="block">
                            ID: {space.externalId}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Selected Value Display */}
      {value && !isOpen && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Selected: {value}
        </Typography>
      )}

      {/* Help Text */}
      {!mappedInAvailable && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Manual input mode - MappedIn integration not available
        </Typography>
      )}
    </Box>
  );
};

export default MappedInSpaceSelector;
